import { useState } from 'react';
import RequestQuotePopup from '../../../components/feature/RequestQuotePopup';

const supportedPlatforms = [
  { name: "SAVANT", icon: "ri-home-wifi-line", color: "from-blue-500/20 to-blue-600/5" },
  { name: "Lutron", icon: "ri-lightbulb-flash-line", color: "from-amber-500/20 to-amber-600/5" },
  { name: "NICE", icon: "ri-apps-2-line", color: "from-emerald-500/20 to-emerald-600/5" },
  { name: "Q-SYS / QSC", icon: "ri-speaker-3-line", color: "from-purple-500/20 to-purple-600/5" },
  { name: "Crestron", icon: "ri-router-line", color: "from-indigo-500/20 to-indigo-600/5" },
  { name: "ELK / DSC", icon: "ri-shield-check-line", color: "from-red-500/20 to-red-600/5" },
  { name: "Unifi / Ubiquiti", icon: "ri-base-station-line", color: "from-cyan-500/20 to-cyan-600/5" }
];

const trainingPlatforms = [
  { name: "Crestron", icon: "ri-router-line" },
  { name: "Control 4", icon: "ri-dashboard-3-line" },
  { name: "Josh.ai", icon: "ri-mic-2-line" },
  { name: "Aurora", icon: "ri-cast-line" },
  { name: "Zenty", icon: "ri-vidicon-line" },
  { name: "RTI", icon: "ri-remote-control-line" }
];

export default function SupportedPlatformsSection() {
  const [quoteService, setQuoteService] = useState<string | null>(null);

  return (
    <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6">
        <div className="border-t border-gray-100 dark:border-slate-800 pt-16">
      {/* Premium Header & Supported Platforms Grid */}
      <div className="flex flex-col lg:flex-row gap-12 items-center mb-24">
        <div className="lg:w-1/3 text-left">
          <div className="inline-flex items-center gap-2 bg-[#e18033]/10 text-[#e18033] px-4 py-2 rounded-full text-sm font-bold mb-6">
            <i className="ri-global-line"></i>
            <span>Platform Expertise</span>
          </div>
          <h3 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight mb-6">
            Ecosystem <br/><span className="text-[#e18033] drop-shadow-sm">Integration</span>
          </h3>
          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            We currently offer remote programming support for several major Residential & Commercial Automation and Building Management systems including Control, Lighting, Networking, Access control, and Security systems.
          </p>
        </div>
        
        <div className="lg:w-2/3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
          {supportedPlatforms.map((platform, idx) => (
            <div 
              key={idx} 
              className="group relative overflow-hidden bg-gray-50 dark:bg-slate-800/80 rounded-2xl p-6 border border-gray-100 dark:border-slate-700 hover:border-[#e18033]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[#e18033]/10 hover:-translate-y-1"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${platform.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className="relative z-10 flex flex-col items-center text-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white dark:bg-slate-900 shadow-sm flex items-center justify-center border border-gray-100 dark:border-slate-700 group-hover:border-[#e18033]/30 group-hover:scale-110 transition-all duration-500">
                  <i className={`${platform.icon} text-2xl text-gray-400 group-hover:text-[#e18033] transition-colors`} />
                </div>
                <span className="font-bold text-gray-900 dark:text-white group-hover:text-[#e18033] transition-colors">
                  {platform.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* In Training Section */}
      <div className="relative mb-20 group">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#e18033]/5 to-transparent rounded-3xl transition-opacity duration-700 group-hover:via-[#e18033]/10" />
        <div className="relative z-10 bg-white/60 dark:bg-slate-800/60 backdrop-blur-md border border-gray-100 dark:border-slate-700/50 rounded-3xl p-8 lg:p-12 transition-all duration-500 hover:border-[#e18033]/20">
          <div className="text-center mb-10">
            <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Continuous Learning</h4>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
              We are constantly training our resources to add more platforms to our list. Our programmers are currently being trained and certified on the following additional platforms:
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {trainingPlatforms.map((platform, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-3 bg-white dark:bg-slate-900 rounded-full px-6 py-3 border border-gray-200 dark:border-slate-700 shadow-sm hover:border-[#e18033]/30 hover:shadow-[#e18033]/5 hover:-translate-y-0.5 transition-all duration-300"
              >
                <i className={`${platform.icon} text-[#e18033]/70`} />
                <span className="font-semibold text-gray-700 dark:text-gray-300">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Premium CTAs */}
      <div className="flex flex-col md:flex-row items-stretch justify-center gap-6">
        <button
          onClick={() => setQuoteService('Programming Support (Other Platform)')}
          className="group relative p-8 bg-white dark:bg-slate-800 text-[#e18033] border-2 border-[#e18033]/20 hover:border-[#e18033] rounded-3xl transition-all duration-500 w-full md:w-1/2 overflow-hidden flex flex-col items-center justify-center gap-3 text-center"
        >
          <div className="absolute inset-0 bg-[#e18033]/5 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
          <i className="ri-question-answer-line text-3xl mb-1 relative z-10 opacity-70 group-hover:opacity-100 transition-opacity" />
          <span className="relative z-10 font-bold text-xl">Inquire About Other Platforms</span>
          <span className="relative z-10 text-sm text-gray-500 dark:text-gray-400 max-w-sm font-medium">
            Click here to let us know if you are looking for programming support on a BAS platform other than the ones listed above.
          </span>
        </button>
        <button
          onClick={() => setQuoteService('Programming Support (Listed Platform)')}
          className="group relative p-8 bg-gradient-to-br from-[#e18033] to-[#c9702a] text-white rounded-3xl transition-all duration-500 shadow-[0_10px_40px_-10px_rgba(225,128,51,0.5)] hover:shadow-[0_20px_60px_-15px_rgba(225,128,51,0.7)] hover:-translate-y-1 w-full md:w-1/2 overflow-hidden flex flex-col items-center justify-center gap-3 text-center"
        >
          <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
          <i className="ri-rocket-line text-3xl mb-1 relative z-10 opacity-90 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
          <span className="relative z-10 font-bold text-xl">Request Platform Support</span>
          <span className="relative z-10 text-sm text-white/80 max-w-sm font-medium">
            Click here to request remote programming support on any of the above listed platforms.
          </span>
        </button>
      </div>

        <RequestQuotePopup
          isOpen={!!quoteService}
          onClose={() => setQuoteService(null)}
          serviceName={quoteService || undefined}
        />
        </div>
      </div>
    </section>
  );
}
